import React from "react";
import { useNavigate } from "react-router-dom";
import { Target, ArrowRight, RotateCcw, Crown } from "lucide-react";

// Pre-boss readiness: tested skills, current progress, and "strengthen this first" links below the line.
export const BossReadiness = ({ items }) => {
  const navigate = useNavigate();
  if (!items?.length) return null;
  return (
    <div className="tc-card p-5 text-left mt-4" data-testid="boss-readiness">
      <div className="text-[11px] font-mono uppercase tracking-widest text-tc-xp flex items-center gap-1.5"><Target className="w-3.5 h-3.5" /> Readiness check</div>
      <p className="text-[12px] text-tc-muted mt-1">Skills this boss tests. Anything below the line is worth strengthening first.</p>
      {items.map((r) => (
        <div key={r.skill} className="mt-3" data-testid={`readiness-${r.skill}`}>
          <div className="flex justify-between text-[13px]">
            <span className="text-tc-sub">{r.label}</span>
            <span className={`font-mono ${r.ready ? "text-tc-bull" : "text-tc-xp"}`}>{Math.round(r.mastery * 100)}% {r.ready ? "· ready" : "· below the line"}</span>
          </div>
          <div className="relative h-1.5 rounded-full bg-white/10 mt-1 overflow-hidden">
            <div className={`h-full rounded-full ${r.ready ? "bg-tc-bull" : "bg-tc-xp"}`} style={{ width: `${r.mastery * 100}%` }} />
            <div className="absolute top-0 bottom-0 w-px bg-white/60" style={{ left: `${(r.ready_line ?? 0.5) * 100}%` }} />
          </div>
          {!r.ready && r.missions.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-1.5">
              {r.missions.map((mm) => (
                <button key={mm.id} data-testid={`strengthen-${r.skill}-${mm.id}`} onClick={() => navigate(`/training/${mm.id}`)}
                  className="text-[12px] text-tc-coach hover:underline flex items-center gap-1">Strengthen this first: {mm.name} <ArrowRight className="w-3 h-3" /></button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Failed boss: targeted practice for the failed skills + a visible retry.
export const BossRetry = ({ practice }) => {
  const navigate = useNavigate();
  if (!practice) return null;
  return (
    <div className="tc-card p-5 border-tc-xp/30" data-testid="boss-retry">
      <div className="text-[11px] font-mono uppercase tracking-widest text-tc-xp">Not passed yet — targeted practice</div>
      <p className="text-sm text-tc-sub mt-1">Strengthen these skills, then try the boss again.</p>
      {practice.skills.map((s) => (
        <div key={s.skill} className="mt-2.5">
          <div className="text-[13px] text-tc-txt">{s.label}</div>
          <div className="flex flex-wrap gap-2 mt-1">
            {s.missions.map((mm) => (
              <button key={mm.id} data-testid={`practice-${s.skill}-${mm.id}`} onClick={() => navigate(`/training/${mm.id}`)}
                className="text-[12px] rounded-md bg-white/5 border border-white/10 px-2.5 py-1 text-tc-coach hover:border-tc-coach/40">Practise: {mm.name}</button>
            ))}
          </div>
        </div>
      ))}
      <button data-testid="boss-retry-btn" onClick={() => navigate(`/training/${practice.retry_id}`)}
        className="mt-4 w-full rounded-lg bg-tc-xp text-tc-bg font-semibold py-2.5 text-sm hover:bg-tc-xp/90 flex items-center justify-center gap-2">
        <RotateCcw className="w-4 h-4" /> Retry the boss
      </button>
    </div>
  );
};

// Stage-entered copy, never identity-granted.
export const StageUnlocked = ({ copy }) => copy ? (
  <div className="tc-card p-6 text-center bg-gradient-to-br from-tc-xp/15 to-transparent border-tc-xp/40 animate-fade-up" data-testid="stage-complete">
    <Crown className="w-8 h-8 text-tc-xp mx-auto" />
    <div className="text-[11px] font-mono uppercase tracking-widest text-tc-muted mt-2">STAGE COMPLETE</div>
    <div className="font-heading text-xl sm:text-2xl font-extrabold text-tc-xp tracking-tight mt-1" data-testid="stage-unlocked-copy">{copy}</div>
  </div>
) : null;

// Boss scorecard: numbers only where there is evidence.
export const BossScorecard = ({ categories, overall }) => categories ? (
  <div className="tc-card p-5" data-testid="boss-scores">
    <div className="flex justify-between items-baseline mb-3">
      <div className="text-[11px] font-mono uppercase tracking-widest text-tc-xp">Boss Scorecard</div>
      {overall != null && <div className="font-mono text-sm" data-testid="boss-overall">{overall}% overall</div>}
    </div>
    {categories.map((c) => (
      <div key={c.category} className="mb-2.5" data-testid={`boss-cat-${c.category.toLowerCase().replace(/\s/g, "-")}`}>
        <div className="flex justify-between text-[13px]"><span className="text-tc-sub">{c.category}</span><span className="font-mono">{c.score == null ? "Not enough evidence yet" : `${c.score}%`}</span></div>
        {c.score != null && <div className="h-1.5 rounded-full bg-white/10 mt-1 overflow-hidden"><div className="h-full bg-tc-xp rounded-full" style={{ width: `${c.score}%` }} /></div>}
        {c.note && <p className="text-[11px] text-tc-muted mt-1">{c.note}</p>}
      </div>
    ))}
  </div>
) : null;
